import React, { useState, useEffect, useRef } from 'react';
import {
    categoriasDisponibles,
    itemsPorCategoria,
    modelosTodos
} from './data/follajeData';

// 12 cabe exacto en 4 columnas (escritorio), 3 (tablet) y 2 (móvil): nunca queda una fila a medias.
const POR_PAGINA = 12;

// Botones de página con área táctil mínima de 44px
const estiloPagina = {
    minWidth: '44px',
    height: '44px',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '14px',
    fontWeight: 600
};

// Páginas a mostrar: 1 … actual-1, actual, actual+1 … última (con "…" donde se omiten)
const getPaginasVisibles = (actual, total) => {
    if (total <= 7) return Array.from({ length: total }, (_, i) => i + 1);
    const paginas = [1];
    const inicio = Math.max(2, actual - 1);
    const fin = Math.min(total - 1, actual + 1);
    if (inicio > 2) paginas.push('…');
    for (let i = inicio; i <= fin; i++) paginas.push(i);
    if (fin < total - 1) paginas.push('…');
    paginas.push(total);
    return paginas;
};

// Animación de entrada para las tarjetas al filtrar o cambiar de página.
// Como el contenedor del grid tiene una `key` que depende de pestaña + página,
// React desmonta y remonta las tarjetas y la animación se dispara de nuevo en cada cambio.
// `prefers-reduced-motion` la desactiva para usuarios que lo pidan.
const ESTILOS_ANIMACION = `
@keyframes espCardIn {
    from {
        opacity: 0;
        transform: translateY(12px) scale(0.98);
    }
    to {
        opacity: 1;
        transform: translateY(0) scale(1);
    }
}

.esp-card-in {
    opacity: 0;
    animation: espCardIn 420ms cubic-bezier(0.22, 1, 0.36, 1) forwards;
    will-change: opacity, transform;
}

@media (prefers-reduced-motion: reduce) {
    .esp-card-in {
        animation: none;
        opacity: 1;
        transform: none;
    }
}
`;

const IconoFormato = () => (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <rect x="3" y="3" width="7" height="7" rx="1" />
        <rect x="14" y="3" width="7" height="7" rx="1" />
        <rect x="3" y="14" width="7" height="7" rx="1" />
        <rect x="14" y="14" width="7" height="7" rx="1" />
    </svg>
);

const IconoPeso = () => (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <path d="M12 2a4 4 0 0 1 4 4v2H8V6a4 4 0 0 1 4-4z" />
        <path d="M5 8h14l1.4 11.2A2 2 0 0 1 18.4 21.5H5.6a2 2 0 0 1-2-2.3L5 8z" />
    </svg>
);

const FollajeTabs = () => {
    // 'todos' = sin filtro; cualquier otro valor es el id de una categoría
    const [activeTab, setActiveTab] = useState('todos');

    const [pagina, setPagina] = useState(1);
    // Las tarjetas (y sus imágenes) no se montan hasta que el catálogo se acerca a la pantalla
    // o el usuario elige una categoría.
    const [activado, setActivado] = useState(false);
    const catalogoRef = useRef(null);
    const inicioRef = useRef(null);

    useEffect(() => {
        if (activado) return undefined;
        const nodo = catalogoRef.current;
        if (!nodo || typeof IntersectionObserver === 'undefined') {
            setActivado(true);
            return undefined;
        }
        const observer = new IntersectionObserver(
            (entradas) => {
                if (entradas.some((e) => e.isIntersecting)) {
                    setActivado(true);
                    observer.disconnect();
                }
            },
            { rootMargin: '300px 0px' } // empieza a cargar poco antes de llegar
        );
        observer.observe(nodo);
        return () => observer.disconnect();
    }, [activado]);

    // Escucha las peticiones de filtrado que llegan desde la tabla de Especificaciones
    // (y desde el botón "Ver catálogo" de la intro).
    // - Si el evento llega antes de que este componente se monte (p. ej. carga diferida),
    //   se recupera la categoría pendiente desde window.__follajeCategoria.
    // - Si llega después del montaje, se aplica directamente vía el evento.
    useEffect(() => {
        const aplicarCategoria = (id) => {
            if (!id) return;
            const existe = categoriasDisponibles.some((cat) => cat.id === id);
            if (!existe) return;
            setActiveTab(id);
            setPagina(1);
            setActivado(true);
        };

        // 1) Petición pendiente de antes del montaje
        if (window.__follajeCategoria) {
            aplicarCategoria(window.__follajeCategoria);
            window.__follajeCategoria = null;
        }

        // 2) Peticiones posteriores
        const handler = (e) => aplicarCategoria(e.detail);
        window.addEventListener('follaje:categoria', handler);
        return () => window.removeEventListener('follaje:categoria', handler);
    }, []);

    const categoriaActiva = categoriasDisponibles.find((cat) => cat.id === activeTab);
    const items = categoriaActiva ? itemsPorCategoria[categoriaActiva.id] : modelosTodos;
    const totalPaginas = Math.max(1, Math.ceil(items.length / POR_PAGINA));
    const paginaActual = Math.min(pagina, totalPaginas);
    const inicioPagina = (paginaActual - 1) * POR_PAGINA;
    const itemsPagina = items.slice(inicioPagina, inicioPagina + POR_PAGINA);

    const cambiarTab = (id) => {
        setActiveTab(id);
        setPagina(1);
        setActivado(true);
    };

    // Al cambiar de página, si las pestañas quedaron arriba de la pantalla, regresa a ellas
    const irAPagina = (n) => {
        setPagina(n);
        const el = inicioRef.current;
        if (el && el.getBoundingClientRect().top < 0) {
            const reducir = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
            el.scrollIntoView({ behavior: reducir ? 'auto' : 'smooth', block: 'start' });
        }
    };

    // Función para construir la ruta de la imagen
    const getImagePath = (categoria, modelo, subcarpeta = '') => {
        const base = `${process.env.PUBLIC_URL}/assets/img/productos/follaje-artificial/${categoria.carpeta}`;
        if (subcarpeta) {
            return `${base}/${subcarpeta}/${modelo.imagen}`;
        }
        return `${base}/${modelo.imagen}`;
    };

    // Tarjeta de modelo: contenedor de imagen a proporción fija (misma altura en toda la grilla)
    // y ficha inferior con iconos.
    // El badge superior muestra la categoría SOLO en la vista "Todos": cuando ya hay una
    // categoría activa, repetir el mismo texto en cada tarjeta se vuelve ruido visual.
    // `delayIndex` escala la animación de entrada (stagger): cada tarjeta entra un poco después
    // que la anterior. Se topa a 11 para que la última no tarde demasiado.
    const renderModeloCard = (modelo, index, categoria, subcarpeta = '', delayIndex = 0) => {
        const delay = Math.min(delayIndex, 11) * 40;

        return (
            <div
                key={`${modelo.codigo}-${index}`}
                className="col-lg-3 col-md-4 col-6 mt-1-9 d-flex esp-card-in"
                style={{ animationDelay: `${delay}ms` }}
            >
                <div className="card border-0 shadow-sm h-100 w-100 overflow-hidden">
                    {/* Imagen a proporción fija 4:3, independiente del tamaño original del archivo */}
                    <div className="position-relative" style={{ aspectRatio: '4 / 3', overflow: 'hidden', backgroundColor: '#eef1ee' }}>
                        <img
                            src={getImagePath(categoria, modelo, subcarpeta)}
                            alt={modelo.nombre}
                            loading="lazy"
                            decoding="async"
                            style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                        />

                        {/* Badge de categoría: solo en la vista "Todos" */}
                        {!categoriaActiva && (
                            <span
                                className="position-absolute top-0 start-0 m-2 bg-white text-dark text-uppercase font-weight-700"
                                style={{ fontSize: '11px', letterSpacing: '0.5px', padding: '4px 10px', borderRadius: '4px', boxShadow: '0 1px 4px rgba(0,0,0,0.15)' }}
                            >
                                {categoria.nombre}
                            </span>
                        )}
                    </div>

                    {/* Ficha del modelo, con footer alineado abajo sin importar el largo del nombre */}
                    <div className="d-flex flex-column flex-grow-1 p-1-6">
                        <h3 className="h6 mb-1 font-weight-700">{modelo.nombre}</h3>
                        <p className="mb-0 text-muted" style={{ fontSize: '11px', letterSpacing: '0.3px' }}>{modelo.codigo}</p>

                        <div className="mt-auto pt-2 mt-2 border-top">
                            <div className="d-flex align-items-center text-secondary mb-1">
                                <IconoFormato />
                                <span className="ms-2 text-sm">{modelo.formato}</span>
                            </div>
                            <div className="d-flex align-items-center text-secondary">
                                <IconoPeso />
                                <span className="ms-2 text-sm">{modelo.peso}</span>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        );
    };

    return (
        // id="catalogo" para que tanto EspecificacionesFollaje como IntroFollajeArtificial
        // puedan hacer scroll hasta aquí. scrollMarginTop evita que un header fijo tape el inicio.
        <section id="catalogo" style={{ scrollMarginTop: '100px' }}>
            {/* Keyframes de entrada para las tarjetas (se inyectan una sola vez) */}
            <style>{ESTILOS_ANIMACION}</style>

            <div className="container">
                {/* Título de la sección */}
                <div className="row justify-content-center mb-2-3">
                    <div className="col-lg-10 text-center">
                        <div className="title-style2 mb-1-6 wow fadeIn" data-wow-delay="100ms">
                            <h2 className="h1">Catálogo de Follaje Artificial con más de 70 modelos</h2>
                        </div>
                        <p className="mb-0 text-secondary">
                            Elige la categoría de follaje para tu proyecto
                        </p>
                    </div>
                </div>

                {/* Pestañas */}
                <div ref={inicioRef} style={{ scrollMarginTop: '100px' }} className="row justify-content-center mb-2-9">
                    <div className="col-lg-10">
                        <ul className="nav nav-pills justify-content-center flex-wrap" role="tablist">
                            <li className="nav-item m-1">
                                <button
                                    className={`nav-link rounded-pill px-3 py-2 ${activeTab === 'todos' ? 'active shadow-sm' : 'border'}`}
                                    onClick={() => cambiarTab('todos')}
                                    aria-selected={activeTab === 'todos'}
                                    type="button"
                                    role="tab"
                                    style={{ fontSize: '14px', fontWeight: 600 }}
                                >
                                    Todos
                                </button>
                            </li>
                            {categoriasDisponibles.map((cat) => (
                                <li className="nav-item m-1" key={cat.id}>
                                    <button
                                        className={`nav-link rounded-pill px-3 py-2 ${activeTab === cat.id ? 'active shadow-sm' : 'border'}`}
                                        onClick={() => cambiarTab(cat.id)}
                                        aria-selected={activeTab === cat.id}
                                        type="button"
                                        role="tab"
                                        style={{ fontSize: '14px', fontWeight: 600 }}
                                    >
                                        {cat.nombre}
                                    </button>
                                </li>
                            ))}
                        </ul>
                    </div>
                </div>

                {/* Catálogo: reserva espacio hasta que se monten las tarjetas (evita saltos de layout) */}
                <div ref={catalogoRef} style={activado ? undefined : { minHeight: '600px' }} aria-busy={!activado}>
                    {activado && (
                        <>
                            {/*
                                La `key` depende de pestaña + página: al cambiar cualquiera de las dos,
                                React desmonta el row completo y lo vuelve a montar, lo que dispara
                                la animación CSS `espCardIn` en cada tarjeta (con stagger).
                            */}
                            <div
                                className="row mt-n1-9"
                                key={`${activeTab}-${paginaActual}`}
                            >
                                {(() => {
                                    let tarjetaIndex = 0;
                                    return itemsPagina.flatMap((item, i) => {
                                        const indice = inicioPagina + i;
                                        const elementos = [];

                                        // En una categoría con subgrupos, rotula cada tramo (también al empezar una página)
                                        if (categoriaActiva && item.grupoTitulo && (i === 0 || itemsPagina[i - 1].grupoTitulo !== item.grupoTitulo)) {
                                            elementos.push(
                                                <div key={`grupo-${indice}`} className={`col-12 ${i === 0 ? 'mt-1-9' : 'mt-2-9'}`}>
                                                    <div className="d-flex align-items-center">
                                                        <span className="text-primary text-uppercase small letter-spacing-4 font-weight-700 me-3">
                                                            {item.grupoTitulo}
                                                        </span>
                                                        <span className="flex-grow-1 border-top"></span>
                                                    </div>
                                                </div>
                                            );
                                        }

                                        elementos.push(
                                            renderModeloCard(item.modelo, indice, item.categoria, item.subcarpeta, tarjetaIndex)
                                        );
                                        tarjetaIndex += 1;

                                        return elementos;
                                    });
                                })()}
                            </div>

                            {/* Paginación (solo si hay más de una página) */}
                            {totalPaginas > 1 && (
                                <div className="mt-2-9 d-flex flex-column align-items-center">
                                    <p className="text-muted mb-2" style={{ fontSize: '14px' }} role="status" aria-live="polite">
                                        Mostrando {inicioPagina + 1}–{Math.min(inicioPagina + POR_PAGINA, items.length)} de {items.length} productos
                                    </p>
                                    <nav aria-label="Paginación del catálogo">
                                        <ul className="pagination flex-wrap justify-content-center mb-0">
                                            <li className={`page-item ${paginaActual === 1 ? 'disabled' : ''}`}>
                                                <button
                                                    type="button"
                                                    className="page-link"
                                                    style={estiloPagina}
                                                    onClick={() => irAPagina(paginaActual - 1)}
                                                    disabled={paginaActual === 1}
                                                >
                                                    Anterior
                                                </button>
                                            </li>
                                            {getPaginasVisibles(paginaActual, totalPaginas).map((n, i) =>
                                                n === '…' ? (
                                                    <li className="page-item disabled" key={`puntos-${i}`}>
                                                        <span className="page-link" style={estiloPagina}>…</span>
                                                    </li>
                                                ) : (
                                                    <li className={`page-item ${n === paginaActual ? 'active' : ''}`} key={n}>
                                                        <button
                                                            type="button"
                                                            className="page-link"
                                                            style={estiloPagina}
                                                            onClick={() => irAPagina(n)}
                                                            aria-label={`Ir a la página ${n}`}
                                                            aria-current={n === paginaActual ? 'page' : undefined}
                                                        >
                                                            {n}
                                                        </button>
                                                    </li>
                                                )
                                            )}
                                            <li className={`page-item ${paginaActual === totalPaginas ? 'disabled' : ''}`}>
                                                <button
                                                    type="button"
                                                    className="page-link"
                                                    style={estiloPagina}
                                                    onClick={() => irAPagina(paginaActual + 1)}
                                                    disabled={paginaActual === totalPaginas}
                                                >
                                                    Siguiente
                                                </button>
                                            </li>
                                        </ul>
                                    </nav>
                                </div>
                            )}
                        </>
                    )}
                </div>
            </div>
        </section>
    );
};

export default FollajeTabs;